export type WorkspaceCompany = {
  id?: string;
  companyId?: string;
  [key: string]: unknown;
};

export type WorkspaceBrand = {
  id?: string;
  brandId?: string;
  companyId?: string | null;
  company?: WorkspaceCompany | null;
  [key: string]: unknown;
};

type WorkspaceSelectionInput<
  Company extends WorkspaceCompany,
  Brand extends WorkspaceBrand,
> = {
  companies: Company[];
  brands: Brand[];
  requestedBrandId?: string | null;
  savedCompanyId?: string | null;
  savedBrandId?: string | null;
};

export const getWorkspaceId = (value?: WorkspaceCompany | WorkspaceBrand | null) =>
  String(value?.id || value?.companyId || value?.brandId || "");

export const getBrandCompanyId = (brand?: WorkspaceBrand | null) =>
  String(
    brand?.companyId ||
      brand?.company?.id ||
      brand?.company?.companyId ||
      "",
  );

export function resolveWorkspaceSelection<
  Company extends WorkspaceCompany,
  Brand extends WorkspaceBrand,
>({
  companies,
  brands,
  requestedBrandId,
  savedCompanyId,
  savedBrandId,
}: WorkspaceSelectionInput<Company, Brand>): {
  company: Company | WorkspaceCompany | null;
  brand: Brand | null;
} {
  const requestedBrand = brands.find(
    (brand) => getWorkspaceId(brand) === requestedBrandId,
  );
  if (requestedBrand) {
    const companyId = getBrandCompanyId(requestedBrand);
    return {
      company:
        companies.find((company) => getWorkspaceId(company) === companyId) ||
        requestedBrand.company ||
        null,
      brand: requestedBrand,
    };
  }

  const savedCompany = companies.find(
    (company) => getWorkspaceId(company) === savedCompanyId,
  );
  const savedBrand = brands.find(
    (brand) =>
      getWorkspaceId(brand) === savedBrandId &&
      (!savedCompany ||
        getBrandCompanyId(brand) === getWorkspaceId(savedCompany)),
  );
  if (savedCompany) {
    return {
      company: savedCompany,
      brand:
        savedBrand ||
        brands.find(
          (brand) =>
            getBrandCompanyId(brand) === getWorkspaceId(savedCompany),
        ) ||
        null,
    };
  }

  const firstCompany = companies[0] || null;
  const firstBrand =
    brands.find(
      (brand) => getBrandCompanyId(brand) === getWorkspaceId(firstCompany),
    ) ||
    brands[0] ||
    null;
  return {
    company: firstCompany || firstBrand?.company || null,
    brand: firstBrand,
  };
}
